import 'moment/locale/pt-br';
import React from 'react';
import { Outlet, useSearchParams } from 'react-router';

import {
  ErrorBoundary,
  ModalContainer,
  ThemeProvider,
  ToastContainer,
} from '@quality24/design-system';
import { Authenticator, DashboardAuthProvider } from '@quality24/react-auth';
import { useStorage } from '@quality24/react-hooks';
import { MaintenanceModeProvider } from '@quality24/react-maintenance-mode';

import { MaintenanceMode } from '@/inpatient-dashboard/molecules/MaintenanceMode';
import { headers } from '@/inpatient-dashboard/services/http';

import MenuProvider from './contexts/menu/MenuProvider';
import WebSocketProvider from './contexts/websocket/WebSocketProvider';
import ExtendedApolloProvider from './providers/ExtendedApolloProvider';

import runtimeConfig from './config';
import { handleOnComponentDidCatch } from './instrumentation';

Authenticator.configure({
  userPoolId:
    runtimeConfig.COGNITO_USER_POOL_ID ||
    process.env.REACT_APP_COGNITO_USER_POOL_ID ||
    '',
  userPoolClientId:
    runtimeConfig.COGNITO_CLIENT_ID ||
    process.env.REACT_APP_COGNITO_CLIENT_ID ||
    '',
  region: runtimeConfig.COGNITO_REGION || process.env.REACT_APP_COGNITO_REGION,
  endpoint:
    runtimeConfig.COGNITO_ENDPOINT || process.env.REACT_APP_COGNITO_ENDPOINT,
});

/**
 * <AppContainer> component
 * This component replaces the Outlet component with the route's element.
 * The element is rendered inside the providers which may depend on react-router,
 * like AuthProvider that uses useNavigate.
 */
const AppContainer: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { persistItem } = useStorage();

  // persist 'embed' into local storage
  persistItem('embed', searchParams.get('embed') || 'false');

  return (
    <ErrorBoundary onError={handleOnComponentDidCatch}>
      <ThemeProvider>
        <ToastContainer autoClose={8000} />
        <MaintenanceModeProvider
          apiURL={runtimeConfig.API_URL || process.env.REACT_APP_API_URL || ''}
          headers={headers}
        >
          <DashboardAuthProvider
            loginUri={
              runtimeConfig.AUTH_SERVICE_URI ||
              process.env.REACT_APP_AUTH_SERVICE_URI ||
              ''
            }
          >
            <ExtendedApolloProvider
              apiURL={
                runtimeConfig.API_URL || process.env.REACT_APP_API_URL || ''
              }
            >
              <WebSocketProvider>
                <MaintenanceMode />
                <MenuProvider>
                  <ModalContainer />
                  <Outlet />
                </MenuProvider>
              </WebSocketProvider>
            </ExtendedApolloProvider>
          </DashboardAuthProvider>
        </MaintenanceModeProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default AppContainer;
